import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogContent, DialogActions, Button, IconButton,
    Typography, Box, TextField, List, ListItem, MenuItem, Select,
    InputLabel, FormControl, Grid
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import {
    DialogTitleStyled,
    CloseIconButtonStyled,
    StyledTextField
} from '../../styles/CalculateMoney/AssignmentCustomerModalStyles';
import { useWorkSessionsContext } from '../../context/WorkSessionsContext';

const UpdateWorkSessionModal = ({
    open,
    onClose,
    workSession,
    businessId,
    services = [],
    categories = [],
    onShowSuccess,
}) => {
    const { t } = useTranslation('updateWorkSessionModal');

    const [designPrice, setDesignPrice] = useState('');
    const [servicesSelected, setServicesSelected] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [serviceToAdd, setServiceToAdd] = useState('');
    const [serviceQuantity, setServiceQuantity] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [note, setNote] = useState('');
    const { updateWorkSessionAndRefresh } = useWorkSessionsContext();

    useEffect(() => {
        setDesignPrice(
            workSession?.personalDesignPrice !== undefined
                ? workSession.personalDesignPrice
                : ''
        );
        setNote(workSession?.note || '');
        let initialServices = [];
        if (workSession?.workSessionServices) {
            if (Array.isArray(workSession.workSessionServices)) {
                initialServices = workSession.workSessionServices;
            } else if (workSession.workSessionServices.$values) {
                initialServices = workSession.workSessionServices.$values.map(svc => ({
                    ...svc,
                    serviceName: svc.service?.name || svc.serviceName || '',
                    quantity: svc.quantity || 1,
                }));
            }
        }
        setServicesSelected(initialServices);
    }, [open, workSession]);

    useEffect(() => {
        setServiceToAdd('');
    }, [selectedCategoryId]);

    const filteredServices = services.filter(
        svc => String(svc.categoryId) === String(selectedCategoryId)
    );

    const handleAddService = () => {
        if (!serviceToAdd) return;
        if (servicesSelected.some(s => String(s.serviceId) === String(serviceToAdd))) return;
        const svc = services.find(s => String(s.serviceId) === String(serviceToAdd));
        if (!svc) return;
        setServicesSelected(prev => [
            ...prev,
            {
                serviceId: svc.serviceId,
                serviceName: svc.name,
                quantity: Number(serviceQuantity) || 1,
            }
        ]);
        setSelectedCategoryId('');
        setServiceToAdd('');
        setServiceQuantity(1);
    };

    const handleRemoveService = (id) => {
        setServicesSelected(prev => prev.filter(svc => String(svc.serviceId) !== String(id)));
    };

    const handleServiceQuantityChange = (serviceId, newQuantity) => {
        setServicesSelected(prev =>
            prev.map(svc =>
                String(svc.serviceId) === String(serviceId)
                    ? { ...svc, quantity: Math.max(1, Number(newQuantity)) }
                    : svc
            )
        );
    };

    const handleFinish = async () => {
        setSubmitting(true);
        try {
            const servicesPayload = servicesSelected.map(svc => ({
                serviceId: svc.serviceId,
                quantity: svc.quantity,
            }));

            const payload = {
                services: servicesPayload,
                designPrice: Number(designPrice) || 0,
                note: note
            };

            await updateWorkSessionAndRefresh(workSession.workSessionId, payload, businessId);

            if (onShowSuccess) onShowSuccess(t('successMessage'));
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitleStyled>
                {t('title')}
                <CloseIconButtonStyled aria-label="close" onClick={onClose}>
                    <CloseIcon />
                </CloseIconButtonStyled>
            </DialogTitleStyled>
            <DialogContent dividers>
                {/* Name and Note */}
                <StyledTextField
                    margin="dense"
                    name="customerName"
                    label={t('fields.customerName')}
                    type="text"
                    fullWidth
                    value={workSession?.customerName || ''}
                    disabled
                />
                <StyledTextField
                    margin="dense"
                    name="note"
                    label={t('fields.note')}
                    type="text"
                    fullWidth
                    value={note}
                    onChange={e => setNote(e.target.value)}
                />

                {/* Service Selection */}
                <Box mb={2} mt={2}>
                    <Typography variant="subtitle1" gutterBottom fontWeight={550}>
                        {t('selectServices')}
                    </Typography>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} sm={5}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="category-label">{t('fields.category')}</InputLabel>
                                <Select
                                    labelId="category-label"
                                    value={selectedCategoryId}
                                    label={t('fields.category')}
                                    onChange={e => setSelectedCategoryId(e.target.value)}
                                    sx={{ background: "#fff" }}
                                >
                                    {categories.map(cat => (
                                        <MenuItem key={cat.categoryId} value={cat.categoryId}>
                                            <Box
                                                sx={{
                                                    display: 'inline-block',
                                                    width: 10,
                                                    height: 10,
                                                    backgroundColor: cat.color,
                                                    borderRadius: '50%',
                                                    mr: 1,
                                                    verticalAlign: 'middle',
                                                }}
                                            />
                                            {cat.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small" disabled={!selectedCategoryId}>
                                <InputLabel id="service-label">{t('fields.service')}</InputLabel>
                                <Select
                                    labelId="service-label"
                                    value={serviceToAdd}
                                    label={t('fields.service')}
                                    onChange={e => setServiceToAdd(e.target.value)}
                                    sx={{ background: "#fff" }}
                                >
                                    {filteredServices.map(svc => (
                                        <MenuItem
                                            key={svc.serviceId}
                                            value={svc.serviceId}
                                            disabled={servicesSelected.some(sel => String(sel.serviceId) === String(svc.serviceId))}
                                        >
                                            {svc.name} &nbsp;
                                            <span style={{ color: '#888', fontSize: '0.9em' }}>
                                                {t('priceUnit', { price: svc.price })}
                                            </span>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField
                                type="number"
                                size="small"
                                label={t('fields.qty')}
                                value={serviceQuantity}
                                onChange={e => setServiceQuantity(Number(e.target.value))}
                                InputProps={{ inputProps: { min: 1 } }}
                                sx={{ background: "#fff" }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={1}>
                            <IconButton
                                color="primary"
                                onClick={handleAddService}
                                size="medium"
                                disabled={!serviceToAdd}
                            >
                                <AddCircleOutlineIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Box>
                {/* Selected Services */}
                <Box mb={3} mt={1}>
                    <Typography variant="subtitle2" fontWeight={550}>{t('selectedServices')}</Typography>
                    <List dense sx={{ mt: 0.5 }}>
                        {(!Array.isArray(servicesSelected) || servicesSelected.length === 0) && (
                            <ListItem>
                                <Typography color="textSecondary" fontSize="0.96em">
                                    {t('noServices')}
                                </Typography>
                            </ListItem>
                        )}
                        {Array.isArray(servicesSelected) && servicesSelected.map((svc, idx) => {
                            const realSvc = services.find(s => String(s.serviceId) === String(svc.serviceId));
                            const displayName = realSvc ? realSvc.name : (svc.serviceName || t('fields.service'));
                            return (
                                <ListItem
                                    key={idx}
                                    sx={{ mb: idx < servicesSelected.length - 1 ? 2 : 0 }}
                                    secondaryAction={
                                        <>
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={svc.quantity}
                                                onChange={e => handleServiceQuantityChange(svc.serviceId, e.target.value)}
                                                inputProps={{ min: 1, style: { width: 40 } }}
                                                sx={{ mx: 1, background: "#fff" }}
                                            />
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleRemoveService(svc.serviceId)}
                                                size="small"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </>
                                    }
                                >
                                    <Typography variant="body2">
                                        {displayName}
                                    </Typography>
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>
                {/* Personal Design Field */}
                <Box mb={1}>
                    <Typography variant="subtitle1" gutterBottom fontWeight={550}>
                        {t('personalDesignLabel')}
                    </Typography>
                    <TextField
                        fullWidth
                        type="number"
                        size="small"
                        value={designPrice}
                        onChange={e => setDesignPrice(e.target.value)}
                        placeholder={t('personalDesignPlaceholder')}
                        InputProps={{ inputProps: { min: 0 } }}
                        sx={{ background: "#fff" }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>{t('buttons.cancel')}</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFinish}
                    disabled={submitting}
                >
                    {t('buttons.update')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UpdateWorkSessionModal;